import {ComponentProps, useCallback, useEffect, useState} from 'react';
import {useScopeTranslation} from '@uigstudio/uig-nextjs-helper';

import {Text} from '~atoms/Text';
import {TextField} from '~atoms/TextField';

import {Container, Field, Separator, TextButton} from './styles';

type FieldRangeProps = {
    placeholder: Record<'min' | 'max', string>;
    type: 'number' | 'currency';
    value: Record<'min' | 'max', number | null>;
    onChange: (value: Record<'min' | 'max', number | null>) => void;
    textFieldProps?: ComponentProps<typeof TextField>;
    className?: string;
    disabled?: boolean;
    isList?: boolean;
};

export const FieldRange = ({
    type,
    value,
    onChange,
    placeholder,
    textFieldProps,
    className,
    disabled,
    isList = false,
}: FieldRangeProps) => {
    const t = useScopeTranslation('common');

    const [min, setMin] = useState(value.min);
    const [max, setMax] = useState(value.max);

    useEffect(() => {
        setMin(value.min);
        setMax(value.max);
    }, [value]);

    const changePrice = useCallback(
        () =>
            onChange({
                min: min ? min : null,
                max,
            }),
        [min, max],
    );

    return (
        <Container className={className}>
            <Field
                type="number"
                empty={min === null}
                value={min}
                onChange={setMin}
                onKeyUp={(e) => e.key === 'Enter' && changePrice()}
                placeholder={placeholder.min}
                currency={type === 'currency'}
                disabled={disabled}
                {...textFieldProps}
                data-cy={`${isList ? 'list-' : 'map-'}min-field`}
            />
            <Separator />
            <Field
                type="number"
                empty={max === null}
                value={max}
                onChange={setMax}
                onKeyUp={(e) => e.key === 'Enter' && changePrice()}
                placeholder={placeholder.max}
                currency={type === 'currency'}
                disabled={disabled}
                data-cy={`${isList ? 'list-' : 'map-'}max-field`}
                {...textFieldProps}
            />
            {min !== value.min || max !== value.max ? (
                <TextButton onClick={changePrice} disabled={disabled}>
                    <Text>{t('apply')}</Text>
                </TextButton>
            ) : min !== null || max !== null ? (
                <TextButton
                    onClick={() => {
                        setMin(null);
                        setMax(null);
                        onChange({
                            min: null,
                            max: null,
                        });
                    }}
                >
                    <Text>{t('clear')}</Text>
                </TextButton>
            ) : (
                <TextButton />
            )}
        </Container>
    );
};
